import { useEffect, useState } from "react";
import { getSettings, setSettings as save, checkAdmin } from "../lib/api";
import { Save, KeyRound, ShieldOff, LogIn } from "lucide-react";

export default function Settings() {
  const [token, setToken] = useState(() => sessionStorage.getItem("wg_admin_token") || "");
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(false);
  const [authMsg, setAuthMsg] = useState("");

  const [current, setCurrent] = useState(null);
  const [key, setKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const tryAuth = async (t) => {
    setChecking(true); setAuthMsg("");
    try {
      const r = await checkAdmin(t);
      if (r.ok) {
        setAuthed(true);
        sessionStorage.setItem("wg_admin_token", t);
        window.dispatchEvent(new Event("wg-admin-changed"));
        const s = await getSettings(t);
        setCurrent(s);
      } else {
        setAuthed(false);
        setAuthMsg("Invalid admin token.");
      }
    } catch (e) {
      setAuthed(false);
      setAuthMsg("Invalid admin token.");
    } finally { setChecking(false); }
  };

  useEffect(() => {
    if (token) tryAuth(token);
    // eslint-disable-next-line
  }, []);

  const onLogin = (e) => { e.preventDefault(); if (token.trim()) tryAuth(token.trim()); };

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true); setMsg("");
    try {
      const r = await save({ safe_browsing_key: key }, token);
      setCurrent(r);
      setKey("");
      setMsg("Saved.");
    } catch (err) {
      setMsg(err?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const logout = () => {
    sessionStorage.removeItem("wg_admin_token");
    window.dispatchEvent(new Event("wg-admin-changed"));
    setToken(""); setAuthed(false); setCurrent(null);
  };

  if (!authed) {
    return (
      <div className="max-w-[560px] mx-auto px-6 py-16">
        <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500">Restricted</div>
        <h1 className="text-4xl md:text-5xl font-black uppercase tracking-tighter" style={{ fontFamily: "'Clash Display'" }}>Admin Only</h1>
        <p className="mt-3 text-sm text-neutral-700">
          The Safe Browsing key and integration settings are configured by WallGard developers.
          End users do not need to access this page.
        </p>
        <form onSubmit={onLogin} className="mt-6 brutal-card p-6" data-testid="admin-login-form">
          <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-neutral-500">
            <LogIn size={14} /> Admin Token
          </div>
          <input
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="enter admin token"
            className="mt-3 w-full border-2 border-black bg-white px-3 py-3 font-mono text-sm rounded-none focus:outline-none focus:bg-black/5"
            data-testid="admin-token-input"
            autoFocus
          />
          <div className="mt-4 flex items-center gap-3">
            <button className="brutal-btn" disabled={checking} data-testid="admin-login-btn">
              {checking ? "Checking…" : "Unlock"}
            </button>
            {authMsg && <div className="text-xs uppercase tracking-widest font-bold text-[#E53935]" data-testid="admin-error">{authMsg}</div>}
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="max-w-[900px] mx-auto px-6 py-10">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500">Configuration · Admin</div>
          <h1 className="text-4xl md:text-5xl font-black uppercase tracking-tighter" style={{ fontFamily: "'Clash Display'" }}>Settings</h1>
        </div>
        <button onClick={logout} className="brutal-btn brutal-btn-outline" data-testid="admin-logout-btn">
          <ShieldOff size={14} /> Lock
        </button>
      </div>

      <form onSubmit={onSave} className="mt-8 brutal-card p-6" data-testid="settings-form">
        <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-neutral-500">
          <KeyRound size={14} /> Google Safe Browsing API Key
        </div>
        <p className="mt-2 text-sm text-neutral-700">
          Optional. When provided, scans are cross-checked against Google's threat database. Get a free key from{" "}
          <a className="underline" href="https://console.cloud.google.com/" target="_blank" rel="noreferrer">Google Cloud Console</a>.
        </p>

        <div className="mt-4 text-xs">
          Current value: <span className="font-mono bg-neutral-100 border-2 border-black px-2 py-1" data-testid="current-key">{current?.safe_browsing_key || "— not set —"}</span>
        </div>

        <input
          value={key}
          onChange={(e) => setKey(e.target.value)}
          placeholder="AIzaSy…"
          className="mt-4 w-full border-2 border-black bg-white px-3 py-3 font-mono text-sm rounded-none focus:outline-none focus:bg-black/5"
          data-testid="safe-browsing-key-input"
        />
        <div className="mt-4 flex items-center gap-3">
          <button className="brutal-btn" disabled={saving} data-testid="settings-save-btn">
            <Save size={14} /> {saving ? "Saving…" : "Save"}
          </button>
          {msg && <div className="text-xs uppercase tracking-widest font-bold">{msg}</div>}
        </div>
      </form>
    </div>
  );
}
